import {
  type InputActionContext,
  resolveInputAction,
} from '../../inputActionContext';
import type {PerfWindowObservationOptions} from './types';

export const INTERACTION_WORKFLOW = 'interaction';

// Capture before application handlers can stop propagation or do expensive work.
// Telemetry never cancels the input's default behavior.
const LISTENER_OPTIONS = {capture: true, passive: true};

/**
 * Starts a window on every trusted click or key press. The observer keys windows by
 * trigger and workflow, so one interaction window runs at a time and later inputs
 * during it count as retriggers. Enablement and cooldown live in the sampling policy.
 */
export function installInteractionTrigger(
  observe: (options: PerfWindowObservationOptions) => boolean
): () => void {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return () => {};
  }

  const onInput = (event: Event) => {
    if (
      !event.isTrusted ||
      (event.type !== 'click' && event.type !== 'keydown')
    ) {
      return;
    }
    const {action, actionSource} = resolveInputAction(event.target);
    const inputContext = {
      inputType: event.type,
      ...(action == null ? {} : {action}),
      ...(actionSource == null ? {} : {actionSource}),
    } satisfies InputActionContext;
    observe({
      trigger: 'interaction',
      workflow: INTERACTION_WORKFLOW,
      initiatingEvent: {name: event.type, startTime: event.timeStamp},
      inputContext,
      // Compatibility aliases for saved searches using the nested fields.
      workflowContext: {
        ...inputContext,
        action: action ?? 'unattributed',
      },
    });
  };

  window.addEventListener('click', onInput, LISTENER_OPTIONS);
  // Passive telemetry observes all trusted keys, including input fields; this is not a shortcut.
  // oxlint-disable-next-line wandb/no-ad-hoc-keydown-listener
  window.addEventListener('keydown', onInput, LISTENER_OPTIONS);
  return () => {
    window.removeEventListener('click', onInput, LISTENER_OPTIONS);
    window.removeEventListener('keydown', onInput, LISTENER_OPTIONS);
  };
}
